/**
 * S3 Upload utility for handling the 3-step upload process
 */

/**
 * Uploads a file with progress tracking using the optimal method
 */
export async function uploadFileToS3(file, presignFn, completeFn, onProgress = null) {
  try {
    // Step 1: Get presigned URL/POST data
    const presignData = await presignFn(file.name);
    console.log('Presign data:', presignData);
    
    // Step 2: Try multiple upload strategies
    let uploadSuccess = false;
    let lastError = null;
    
    // Strategy 1: Try POST if available
    if (presignData.method === "POST" && presignData.fields) {
      try {
        console.log('Trying POST upload...');
        await uploadWithPost(file, presignData, onProgress);
        uploadSuccess = true;
      } catch (error) {
        console.log('POST upload failed:', error.message);
        lastError = error;
      }
    }
    
    // Strategy 2: Try PUT if POST failed or not available
    if (!uploadSuccess) {
      try {
        console.log('Trying PUT upload...');
        await uploadWithPut(file, presignData, onProgress);
        uploadSuccess = true;
      } catch (error) {
        console.log('PUT upload failed:', error.message);
        lastError = error;
      }
    }
    
    // Strategy 3: Try fetch as fallback
    if (!uploadSuccess) {
      try {
        console.log('Trying fetch upload...');
        await uploadWithFetch(file, presignData, onProgress);
        uploadSuccess = true;
      } catch (error) {
        console.log('Fetch upload failed:', error.message);
        lastError = error;
      }
    }
    
    if (!uploadSuccess) {
      throw lastError || new Error('All upload strategies failed');
    }
    
    // Step 3: Complete upload
    const completeData = {
      objectKey: presignData.objectKey,
      filename: file.name
    };
    
    const result = await completeFn(completeData);
    return result;
    
  } catch (error) {
    console.error('S3 upload with progress failed:', error);
    throw error;
  }
}

async function uploadWithPost(file, presignData, onProgress) {
  const formData = new FormData();
  
  // Add all the required fields from the presigned response
  if (presignData.fields) {
    Object.keys(presignData.fields).forEach(key => {
      formData.append(key, presignData.fields[key]);
    });
  }
  
  // Add the file last
  formData.append('file', file);
  
  console.log('POST FormData fields:', Array.from(formData.keys()));
  
  // Use XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });
    
    xhr.addEventListener('load', () => {
      console.log('POST response status:', xhr.status);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`POST upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });
    
    xhr.addEventListener('error', (event) => {
      console.error('POST upload error event:', event);
      reject(new Error('POST upload failed'));
    });
    
    xhr.open('POST', presignData.uploadUrl);
    xhr.send(formData);
  });
}

async function uploadWithPut(file, presignData, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });
    
    xhr.addEventListener('load', () => {
      console.log('PUT response status:', xhr.status);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`PUT upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });
    
    xhr.addEventListener('error', (event) => {
      console.error('PUT upload error event:', event);
      reject(new Error('PUT upload failed'));
    });
    
    xhr.open('PUT', presignData.uploadUrl);
    // Don't set any headers to avoid CORS preflight
    xhr.send(file);
  });
}

async function uploadWithFetch(file, presignData, onProgress) {
  console.log('Attempting fetch upload without custom headers...');
  
  const response = await fetch(presignData.uploadUrl, {
    method: 'PUT',
    body: file,
    mode: 'cors'
    // No headers to avoid preflight
  });
  
  console.log('Fetch response status:', response.status);
  
  if (!response.ok) {
    throw new Error(`Fetch upload failed: ${response.status} ${response.statusText}`);
  }
}

/**
 * Validates file type and size
 * 
 * @param {File} file - The file to validate
 * @param {string[]} allowedTypes - Array of allowed MIME types or extensions
 * @param {number} maxSizeMB - Maximum file size in MB (default: 50)
 * @returns {boolean} - True if valid
 * @throws {Error} - If validation fails
 */
export function validateFile(file, allowedTypes = [], maxSizeMB = 50) {
  if (!file) {
    throw new Error('No file provided');
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    throw new Error(`File size must be less than ${maxSizeMB}MB`);
  }
  
  // Check file type if restrictions are provided
  if (allowedTypes.length > 0) {
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const mimeType = file.type;
    
    const isValidType = allowedTypes.some(type => 
      type.startsWith('.') ? type === fileExtension : type === mimeType
    );
    
    if (!isValidType) {
      throw new Error(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`);
    }
  }
  
  return true;
} 